import {testIdProps} from '@github-ui/test-id-props'
import {Button, FormControl, Heading, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {useCallback, useEffect, useEffectEvent, useRef} from 'react'

import {useNewWorkflowDialog, useWorkflows} from '../../state-providers/workflows/use-workflows'
import {CreateWorkflowResources} from '../../strings'
import styles from './create-new-workflow-dialog.module.css'
import {useWorkflowNameValidation} from './hooks/use-workflow-name-validation'

type CreateNewWorkflowDialogProps = {
  setStartInEditMode: (editing: boolean) => void
}

export const CreateNewWorkflowDialog = ({setStartInEditMode}: CreateNewWorkflowDialogProps) => {
  const {
    isDialogOpen,
    setIsDialogOpen,
    workflowName,
    setWorkflowName,
    workflowTemplate,
    applyWorkflowTemplate,
    returnFocusClientId,
  } = useNewWorkflowDialog()

  const {workflowNames, activeWorkflow, newWorkflow, handleReturnFocus} = useWorkflows()
  const {error, onChangeHandler, validate} = useWorkflowNameValidation({
    onWorkflowNameChange: setWorkflowName,
    workflowNames: activeWorkflow ? new Set(workflowNames).add(activeWorkflow.name.toLocaleLowerCase()) : workflowNames,
  })

  const initialFocusRef = useRef<HTMLInputElement>(null)

  const createWorkflow = useCallback(() => {
    newWorkflow(workflowName, 'query_matched_add_project_item', workflowTemplate)
    setIsDialogOpen(false)
    setStartInEditMode(true)
  }, [newWorkflow, setStartInEditMode, setIsDialogOpen, workflowName, workflowTemplate])

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false)
    // Reset workflow template
    applyWorkflowTemplate()

    handleReturnFocus(returnFocusClientId)
  }, [applyWorkflowTemplate, setIsDialogOpen, handleReturnFocus, returnFocusClientId])

  const validateOnOpen = useEffectEvent(() => validate(workflowName))
  useEffect(() => {
    if (isDialogOpen) {
      validateOnOpen()
    }
    // We only want to run this effect when the dialog is opened
  }, [isDialogOpen])

  return (
    <Dialog
      isOpen={isDialogOpen}
      initialFocusRef={initialFocusRef}
      onDismiss={closeDialog}
      aria-labelledby="new-workflow-header"
    >
      <Dialog.Header id="new-workflow-header" className={styles.Dialog_Header}>
        <Heading as="h3" className={styles.Heading}>
          {CreateWorkflowResources.duplicateWorkflowLabel}
        </Heading>
      </Dialog.Header>
      <div className={styles.Box}>
        <FormControl className={styles.FormControl}>
          <FormControl.Label>{CreateWorkflowResources.workflowNameInputLabel}</FormControl.Label>
          <TextInput
            ref={initialFocusRef}
            value={workflowName}
            onChange={onChangeHandler}
            validationStatus={error ? 'error' : undefined}
            className={styles.TextInput}
            {...testIdProps('new-workflow-dialog-input')}
          />
          {error && (
            <p className={styles.Text} {...testIdProps('new-workflow-dialog-error')}>
              {error}
            </p>
          )}
        </FormControl>
      </div>
      <div className={styles.Box_1}>
        <Button
          disabled={!!error}
          variant="primary"
          onClick={createWorkflow}
          {...testIdProps('new-workflow-dialog-create-button')}
        >
          {CreateWorkflowResources.duplicateButton}
        </Button>
      </div>
    </Dialog>
  )
}
